import isEqual from 'lodash/isEqual';
import {
  CaseContext,
  IssueCoding,
  ProtocolFormData,
  ProtocolStatus,
  RelevanceCriteria,
} from '../../../../../redux/slice/aiReviewSliceV2';
import {
  ProtocolVersionDetailsResponseV2,
  UpdateProtocolVersionDetailsPayloadV2,
  ICreateProtocolIntelligencePayload,
  ICreateProtocolIntelligenceFileDetails,
  ICreateProtocolVersionPayload,
  ProtocolFileDetailsV2,
} from '../../../../../api/types/aiReview.type';

/**
 * Parses "V1" / "v2" / "3" → number. Returns 0 when missing/invalid.
 */
export const parseProtocolVersionNumber = (
  latestVersion?: string | null
): number => {
  if (!latestVersion) {
    return 0;
  }
  const match = String(latestVersion).match(/(\d+)/);
  return match ? Number(match[1]) : 0;
};

/**
 * Version dropdown / Version History:
 * - Approved (V1 or higher): always show
 * - Draft V1 (single version): hide
 * - Draft V2+ or multiple versions: show
 */
export const shouldShowProtocolVersionControls = (
  latestVersion?: string | null,
  versionsCount = 0,
  status?: ProtocolStatus | string | null
): boolean => {
  const normalized = status?.toString().trim().toLowerCase();
  if (normalized === 'approved') {
    return true;
  }
  return parseProtocolVersionNumber(latestVersion) > 1 || versionsCount > 1;
};

const diffObjectKeys = <T extends Record<string, unknown>>(
  current: T,
  initial: T
): Partial<T> | undefined => {
  const changed: Partial<T> = {};
  let hasChanges = false;

  (Object.keys(current) as Array<keyof T>).forEach((key) => {
    if (!isEqual(current[key], initial[key])) {
      changed[key] = current[key];
      hasChanges = true;
    }
  });

  return hasChanges ? changed : undefined;
};

const omitProtocolIdentity = (
  protocol: ProtocolFormData
): Omit<ProtocolFormData, 'protocolId' | 'protocolVersionId'> => {
  const rest = {...protocol};
  delete rest.protocolId;
  delete rest.protocolVersionId;
  return rest;
};

/** True when a value is blank UI chrome (empty string / empty nested structure). */
const isEmptyPlaceholderValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  if (Array.isArray(value)) {
    return value.length === 0 || value.every(isEmptyPlaceholderValue);
  }
  if (typeof value === 'object') {
    const entries = Object.values(value as Record<string, unknown>);
    return entries.length === 0 || entries.every(isEmptyPlaceholderValue);
  }
  return false;
};

/**
 * Strips empty UI placeholder rows (e.g. seeded `{id:''}` / `''`) so dirty
 * compare ignores form chrome and only meaningful edits remain.
 */
export const normalizeSectionForDirtyDiff = <T extends Record<string, unknown>>(
  section: T | null | undefined
): T => {
  if (!section || typeof section !== 'object' || Array.isArray(section)) {
    return {} as T;
  }

  const normalized: Record<string, unknown> = {};

  Object.entries(section).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      normalized[key] = value.filter((item) => !isEmptyPlaceholderValue(item));
      return;
    }
    normalized[key] = value;
  });

  return normalized as T;
};

/**
 * AVR-21735 partial create body: drop empty strings / empty arrays / empty objects
 * so unfilled sections become `{}` and filled sections only include provided keys.
 */
export const compactPartialSection = (
  value: unknown
): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  const result: Record<string, unknown> = {};

  Object.entries(value as Record<string, unknown>).forEach(([key, entry]) => {
    if (entry === null || entry === undefined) {
      return;
    }

    if (typeof entry === 'string') {
      if (entry.trim() !== '') {
        result[key] = entry;
      }
      return;
    }

    if (Array.isArray(entry)) {
      if (entry.length === 0) {
        return;
      }

      const compactedItems = entry
        .map((item) => {
          if (item && typeof item === 'object' && !Array.isArray(item)) {
            return compactPartialSection(item);
          }
          if (typeof item === 'string') {
            return item.trim();
          }
          return item;
        })
        .filter((item) => {
          if (typeof item === 'string') {
            return item !== '';
          }
          if (item && typeof item === 'object' && !Array.isArray(item)) {
            return Object.keys(item).length > 0;
          }
          return item !== null && item !== undefined;
        });

      if (compactedItems.length > 0) {
        result[key] = compactedItems;
      }
      return;
    }

    if (typeof entry === 'object') {
      const nested = compactPartialSection(entry);
      if (Object.keys(nested).length > 0) {
        result[key] = nested;
      }
      return;
    }

    result[key] = entry;
  });

  return result;
};

const resolveProtocolArtifactId = (
  fileDetails?: ProtocolFileDetailsV2 | ProtocolFormData['fileDetails'] | null
): number | null => {
  if (!fileDetails) {
    return null;
  }
  if (
    'protocolArtifactId' in fileDetails &&
    typeof fileDetails.protocolArtifactId === 'number'
  ) {
    return fileDetails.protocolArtifactId;
  }
  if (
    'artifactId' in fileDetails &&
    typeof fileDetails.artifactId === 'number'
  ) {
    return fileDetails.artifactId;
  }
  if (
    'reviewProtocolDocumentID' in fileDetails &&
    fileDetails.reviewProtocolDocumentID != null &&
    fileDetails.reviewProtocolDocumentID !== ''
  ) {
    const parsed = Number(fileDetails.reviewProtocolDocumentID);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * Extracts basename with extension from a full path or UNC path.
 * e.g. `\\server\share\folder\File.doc` → `File.doc`
 */
export const getProtocolFileDisplayName = (
  fileName?: string | null
): string => {
  const trimmed = fileName?.trim() ?? '';
  if (!trimmed) {
    return '';
  }
  const normalized = trimmed.replace(/\\/g, '/');
  const segments = normalized.split('/');
  return segments[segments.length - 1] || trimmed;
};

/** Builds fileDetails for create/edit only when a protocol file was uploaded. */
export const buildFileDetailsPayload = (
  fileDetails?: ProtocolFormData['fileDetails'] | ProtocolFileDetailsV2 | null
): ICreateProtocolIntelligenceFileDetails | undefined => {
  const fileName = getProtocolFileDisplayName(fileDetails?.fileName);
  const artifactId = resolveProtocolArtifactId(fileDetails);
  if (!fileName && artifactId == null) {
    return undefined;
  }
  return {
    fileName,
    artifactId,
  };
};

/** Clears a previously uploaded protocol file on edit/save (Source → Manual). */
export const EMPTY_PROTOCOL_FILE_DETAILS: ICreateProtocolIntelligenceFileDetails =
  {
    fileName: '',
    artifactId: null,
  };

type PersistedProtocolIdSource = {
  protocolId?: number;
  versionId?: number;
  protocolVersionId?: number;
};

type PersistedProtocolIdFallback = {
  protocolId?: number;
  protocolVersionId?: number;
};

/** Normalizes create/update/version API ids and never drops known persisted ids. */
export const resolvePersistedProtocolIds = (
  source: PersistedProtocolIdSource = {},
  ...fallbacks: Array<PersistedProtocolIdFallback | undefined>
): {protocolId?: number; protocolVersionId?: number} => {
  const resolvedVersionId =
    source.versionId ??
    source.protocolVersionId ??
    fallbacks.reduce<number | undefined>(
      (current, fallback) =>
        current ?? fallback?.protocolVersionId ?? undefined,
      undefined
    );

  const resolvedProtocolId = fallbacks.reduce<number | undefined>(
    (current, fallback) => current ?? fallback?.protocolId ?? undefined,
    source.protocolId
  );

  return {
    protocolId: resolvedProtocolId,
    protocolVersionId: resolvedVersionId,
  };
};

export const mapProtocolVersionDetailsToFormData = (
  response: ProtocolVersionDetailsResponseV2,
  ids?: {protocolId?: number; protocolVersionId?: number}
): ProtocolFormData => {
  const fileDetailsPayload = buildFileDetailsPayload(response.fileDetails);
  return {
    protocolId: ids?.protocolId,
    protocolVersionId: ids?.protocolVersionId,
    protocolName: response.protocolName ?? '',
    caseContext: response.caseContext,
    relevanceCriteria: response.relevanceCriteria,
    issueCoding: response.issueCoding,
    status: response.status,
    ...(fileDetailsPayload ? {fileDetails: fileDetailsPayload} : {}),
  };
};

/**
 * AVR-21735 create payload — matches partial examples:
 * empty sections → {}, only filled keys in sections.
 * fileDetails included only when a protocol file was uploaded.
 */
export const buildCreateProtocolPayload = (
  protocol: ProtocolFormData,
  status: ProtocolStatus
): ICreateProtocolIntelligencePayload => {
  const fileDetails = buildFileDetailsPayload(protocol.fileDetails);
  return {
    protocolName: protocol.protocolName.trim(),
    caseContext: compactPartialSection(
      protocol.caseContext
    ) as Partial<CaseContext>,
    relevanceCriteria: compactPartialSection(
      protocol.relevanceCriteria
    ) as Partial<RelevanceCriteria>,
    issueCoding: compactPartialSection(
      protocol.issueCoding
    ) as Partial<IssueCoding>,
    status,
    ...(fileDetails ? {fileDetails} : {}),
  };
};

/**
 * AVR-21867 create-version payload — copies current form sections as partial
 * (compacted) objects with parentVersionId and status (typically draft).
 */
export const buildCreateProtocolVersionPayload = (
  protocolId: number,
  parentVersionId: number,
  protocol: ProtocolFormData,
  status: ProtocolStatus = 'draft'
): ICreateProtocolVersionPayload => {
  const fileDetails = buildFileDetailsPayload(protocol.fileDetails);
  return {
    protocolId,
    parentVersionId,
    caseContext: compactPartialSection(
      protocol.caseContext
    ) as Partial<CaseContext>,
    relevanceCriteria: compactPartialSection(
      protocol.relevanceCriteria
    ) as Partial<RelevanceCriteria>,
    issueCoding: compactPartialSection(
      protocol.issueCoding
    ) as Partial<IssueCoding>,
    status,
    ...(fileDetails ? {fileDetails} : {}),
  };
};

/** Derives next display label (v{n+1}) from existing version names. */
export const computeNextVersionName = (
  versions: Array<{name: string}>
): string => {
  let max = 0;
  versions.forEach((version) => {
    const match = /^v(\d+)$/i.exec(version.name.trim());
    if (match) {
      max = Math.max(max, Number(match[1]));
    }
  });
  return `v${max + 1}`;
};

/**
 * Sentinel version id for an in-memory revision that has not been POSTed yet.
 * Must not be used for GET version-details.
 */
export const UNSAVED_REVISION_VERSION_ID = -1;

/** Deep-clone protocol form data for revise snapshot / working copy. */
export const cloneProtocolFormData = <T>(value: T): T => {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
};

/**
 * Builds a partial PUT body per AVR-21681 dirty rules:
 * - primitives only when changed
 * - nested object keys only when changed
 * - arrays sent in full when any element differs
 * - relevance/issueCoding ignore empty UI placeholder rows when comparing
 * Returns null when there is nothing to save.
 */
export const buildProtocolDirtyPayload = (
  protocol: ProtocolFormData,
  initialProtocol: ProtocolFormData,
  statusOverride?: ProtocolStatus
): UpdateProtocolVersionDetailsPayloadV2 | null => {
  const versionId = protocol.protocolVersionId;
  if (!versionId) {
    return null;
  }

  const payload: UpdateProtocolVersionDetailsPayloadV2 = {versionId};
  let hasChanges = false;

  if (protocol.protocolName !== initialProtocol.protocolName) {
    payload.protocolName = protocol.protocolName;
    hasChanges = true;
  }

  const caseContextDiff = diffObjectKeys(
    protocol.caseContext as unknown as Record<string, unknown>,
    initialProtocol.caseContext as unknown as Record<string, unknown>
  ) as Partial<CaseContext> | undefined;
  if (caseContextDiff) {
    payload.caseContext = caseContextDiff;
    hasChanges = true;
  }

  const relevanceDiff = diffObjectKeys(
    normalizeSectionForDirtyDiff(
      protocol.relevanceCriteria as unknown as Record<string, unknown>
    ),
    normalizeSectionForDirtyDiff(
      initialProtocol.relevanceCriteria as unknown as Record<string, unknown>
    )
  ) as Partial<RelevanceCriteria> | undefined;
  if (relevanceDiff) {
    payload.relevanceCriteria = relevanceDiff;
    hasChanges = true;
  }

  const issueCodingDiff = diffObjectKeys(
    normalizeSectionForDirtyDiff(
      protocol.issueCoding as unknown as Record<string, unknown>
    ),
    normalizeSectionForDirtyDiff(
      initialProtocol.issueCoding as unknown as Record<string, unknown>
    )
  ) as Partial<IssueCoding> | undefined;
  if (issueCodingDiff) {
    payload.issueCoding = issueCodingDiff;
    hasChanges = true;
  }

  const currentFileDetails = buildFileDetailsPayload(protocol.fileDetails);
  const initialFileDetails = buildFileDetailsPayload(
    initialProtocol.fileDetails
  );
  if (!isEqual(currentFileDetails ?? null, initialFileDetails ?? null)) {
    payload.fileDetails =
      currentFileDetails ?? EMPTY_PROTOCOL_FILE_DETAILS;
    hasChanges = true;
  }

  const nextStatus = statusOverride ?? protocol.status;
  if (nextStatus !== initialProtocol.status) {
    hasChanges = true;
  }

  if (!hasChanges) {
    return null;
  }

  // Ticket examples always include status when any section is saved
  payload.status = nextStatus;
  return payload;
};

/** Dirty check that ignores empty relevance/issueCoding UI placeholder rows. */
export const areProtocolFormsEqual = (
  current?: ProtocolFormData | null,
  initial?: ProtocolFormData | null
): boolean => {
  if (!current && !initial) {
    return true;
  }
  if (!current || !initial) {
    return false;
  }

  const normalizeProtocol = (protocol: ProtocolFormData) => {
    const withoutIds = omitProtocolIdentity(protocol);
    const normalizedFileDetails = buildFileDetailsPayload(protocol.fileDetails);
    const {fileDetails: _fileDetails, ...rest} = withoutIds;
    return {
      ...rest,
      ...(normalizedFileDetails ? {fileDetails: normalizedFileDetails} : {}),
      relevanceCriteria: normalizeSectionForDirtyDiff(
        (protocol.relevanceCriteria ?? {}) as unknown as Record<string, unknown>
      ),
      issueCoding: normalizeSectionForDirtyDiff(
        (protocol.issueCoding ?? {}) as unknown as Record<string, unknown>
      ),
    };
  };

  return isEqual(normalizeProtocol(current), normalizeProtocol(initial));
};
